import CloseIcon from '@mui/icons-material/Close';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import axios from 'axios';
import { type FC, useState } from 'react';

import VisionSnippetRow from './VisionSnippetRow.js';
import { useRecordVisionSnippet, useVisionSnippets, VISION_SNIPPET_DURATIONS } from '../../api/index.js';
import VisionReplayDialog, { type ReplayTarget } from '../Activity/VisionReplayDialog.js';

interface Props {
  cameraId: string;
  cameraName: string;
  onClose: () => void;
}

const DEFAULT_DURATION_SECONDS = 8;

function errorMessage(error: unknown): string {
  const data: unknown = axios.isAxiosError(error) ? error.response?.data : null;
  const message = data && typeof data === 'object' && 'message' in data ? data.message : null;
  return typeof message === 'string' ? message : 'The camera could not be recorded';
}

const CameraVisionSnippetsDialog: FC<Props> = ({ cameraId, cameraName, onClose }) => {
  const [durationSeconds, setDurationSeconds] = useState(DEFAULT_DURATION_SECONDS);
  const [replayTarget, setReplayTarget] = useState<ReplayTarget | null>(null);
  const { data: snippets = [], isLoading } = useVisionSnippets(cameraId, true);
  const record = useRecordVisionSnippet(cameraId);

  const recordAndReplay = () => {
    record.mutate(durationSeconds, {
      onSuccess: (snippet) => setReplayTarget({ source: { kind: 'snippet', id: snippet.id }, cameraId, cameraName }),
    });
  };

  return (
    <Dialog open onClose={onClose} maxWidth={'sm'} fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', py: 1 }}>
        <Stack sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant={'subtitle1'}>Vision snippets</Typography>
          <Typography variant={'caption'} color={'text.secondary'} noWrap>{cameraName}</Typography>
        </Stack>
        <IconButton onClick={onClose} edge={'end'} size={'small'} aria-label={'Close'}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 2 }}>
        <Stack direction={'row'} spacing={2} alignItems={'center'} sx={{ mb: 2 }}>
          <TextField
            select
            size={'small'}
            label={'Length'}
            value={durationSeconds}
            onChange={(event) => setDurationSeconds(Number(event.target.value))}
            sx={{ minWidth: 120 }}
          >
            {VISION_SNIPPET_DURATIONS.map((seconds) => <MenuItem key={seconds} value={seconds}>{seconds} s</MenuItem>)}
          </TextField>
          <Button
            variant={'contained'}
            disabled={record.isLoading}
            startIcon={record.isLoading ? <CircularProgress size={16} /> : <FiberManualRecordIcon />}
            onClick={recordAndReplay}
          >
            {record.isLoading ? 'Recording…' : 'Record and replay'}
          </Button>
        </Stack>
        {record.isError && <Alert severity={'error'} sx={{ mb: 2 }}>{errorMessage(record.error)}</Alert>}

        <Typography variant={'overline'} color={'text.secondary'}>Recent snippets</Typography>
        {isLoading && <Box sx={{ display: 'grid', placeItems: 'center', py: 3 }}><CircularProgress size={24} /></Box>}
        {!isLoading && snippets.length === 0 && (
          <Typography variant={'body2'} color={'text.secondary'}>No snippets recorded for this camera yet.</Typography>
        )}
        <Stack spacing={1}>
          {snippets.map((snippet) => (
            <VisionSnippetRow
              key={snippet.id}
              snippet={snippet}
              onReplay={() => setReplayTarget({ source: { kind: 'snippet', id: snippet.id }, cameraId, cameraName })}
            />
          ))}
        </Stack>
      </DialogContent>
      <VisionReplayDialog target={replayTarget} onClose={() => setReplayTarget(null)} />
    </Dialog>
  );
};

export default CameraVisionSnippetsDialog;
